/**
 * Workflow Runner — orchestrates a DSL workflow as a sequence of task executions
 *
 * Programmatic control flow that replaces LLM-driven step interpretation:
 *
 *   1. Load workflow contract (ordered steps)
 *   2. Run plugin beforeWorkflow hooks
 *   3. For each step:
 *      - delegate → call task-runner with followUp/retry recovery
 *      - gate    → pause and wait for user input
 *   4. Validate each result against handoff schema
 *   5. Accumulate context between steps
 *   6. Run plugin afterWorkflow hooks
 */

import { pluginRegistry } from "./plugin.js";
import { runTask, type SdkAdapter, type TaskContext, type TaskOutcome, type TaskRunResult, type AgentContractLike, type TaskContractLike } from "./task-runner.js";
import type { ModelClass } from "./model-resolver.js";
import type { z } from "zod";

// ---------------------------------------------------------------------------
// Adapter factory — enables true parallelism with stateful adapters
// ---------------------------------------------------------------------------

/**
 * A factory that creates independent SdkAdapter instances.
 * Used by runWorkflow to allocate one adapter per parallel step,
 * avoiding session-level serialization in stateful SDK adapters.
 */
export type SdkAdapterFactory = () => SdkAdapter;

/**
 * Model-aware adapter factory. Accepts a model_class and returns an adapter
 * configured for that capability level (possibly a different provider/model).
 */
export type ModelAwareSdkAdapterFactory = (modelClass?: ModelClass) => SdkAdapter | Promise<SdkAdapter>;

/** Accepts either a single adapter (serial/shared) or a factory (parallel-safe). */
export type AdapterOrFactory = SdkAdapter | SdkAdapterFactory | ModelAwareSdkAdapterFactory;

function isAdapterFactory(v: AdapterOrFactory): v is SdkAdapterFactory | ModelAwareSdkAdapterFactory {
  return typeof v === "function" && !("send" in v);
}

function resolveAdapter(adapterOrFactory: AdapterOrFactory): { adapter: SdkAdapter; factory: (SdkAdapterFactory | ModelAwareSdkAdapterFactory) | undefined } {
  if (isAdapterFactory(adapterOrFactory)) {
    const result = adapterOrFactory();
    if (result && typeof (result as Promise<SdkAdapter>).then === "function") {
      throw new Error("ModelAwareSdkAdapterFactory returned a Promise for the default adapter. Use a synchronous factory or pre-resolve the default adapter.");
    }
    return { adapter: result as SdkAdapter, factory: adapterOrFactory };
  }
  return { adapter: adapterOrFactory, factory: undefined };
}

// ---------------------------------------------------------------------------
// Step types (duck-typed — generated contracts provide concrete values)
// ---------------------------------------------------------------------------

export interface DelegateStep {
  readonly type: "delegate";
  readonly task: string;
  readonly from_agent: string;
  readonly description: string;
  readonly optional: boolean;
  readonly max_retries: number;
  readonly max_follow_ups?: number;
  readonly depends_on?: readonly string[];
  readonly retry?: {
    readonly condition: string;
    readonly fix_task: string;
    readonly revalidate_task?: string;
  };
}

export interface GateStep {
  readonly type: "gate";
  readonly gate_kind: string;
  readonly description: string;
  readonly depends_on?: readonly string[];
}

export type WorkflowStep = DelegateStep | GateStep;

export interface WorkflowContractLike {
  readonly id: string;
  readonly description: string;
  readonly steps: readonly WorkflowStep[];
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WorkflowRunOptions {
  user_request: string;
  /** Override max follow-up attempts per step (lightweight output-format correction). Default: 2 */
  maxFollowUps?: number;
  /** Override max full retry attempts per step. Default: from DSL step.max_retries */
  maxRetries?: number;
  /** Variables propagated to every TaskContext in the workflow (from WorkflowInvocation.context). */
  variables?: Record<string, unknown>;
  onGate?: (gateKind: string, description: string, stepResults: StepResult[]) => Promise<boolean>;
  onStepComplete?: (event: StepCompleteEvent) => void;
  onOptionalStep?: (step: DelegateStep, context: TaskContext) => Promise<boolean>;
  /**
   * Writable stream for real-time progress output during agent execution.
   * Intermediate events from adapters are formatted and written here.
   * Typical value: process.stderr.
   */
  progressOutput?: { write(chunk: string): unknown };
}

// ---------------------------------------------------------------------------
// WorkflowInvocation — SDK-independent structured envelope (G3 / G1)
// ---------------------------------------------------------------------------

/** Typed handoff envelope for workflow input. */
export interface HandoffInput<
  TType extends string = string,
  TPayload = unknown,
> {
  readonly type: TType;
  readonly version?: number;
  readonly payload: TPayload;
}

/**
 * Standard runtime invocation envelope. SDK-specific options (openaiThreadId,
 * sessionId, etc.) belong on the adapter constructor — not here.
 */
export interface WorkflowInvocation<
  THandoffType extends string = string,
  TPayload = unknown,
> {
  workflow: string;

  /** Structured typed handoff input. Takes precedence over user_request if both are set. */
  handoff?: HandoffInput<THandoffType, TPayload>;

  /** Simple string input (convenience shorthand). */
  user_request?: string;

  runtime?: {
    maxFollowUps?: number;
    maxRetries?: number;
    timeoutMs?: number;
    readonly?: boolean;
    dryRun?: boolean;
  };

  hooks?: {
    onStepComplete?: (event: StepCompleteEvent) => void;
    onGate?: (gateKind: string, description: string, stepResults: StepResult[]) => Promise<boolean>;
    onOptionalStep?: (step: DelegateStep, context: TaskContext) => Promise<boolean>;
  };

  /**
   * Writable stream for real-time progress output during agent execution.
   * Intermediate events from adapters are formatted and written here.
   * Typical value: process.stderr.
   */
  progressOutput?: { write(chunk: string): unknown };

  context?: {
    cwd?: string;
    environment?: "local" | "ci" | "remote";
    artifacts?: Record<string, string>;
    variables?: Record<string, unknown>;
  };
}

export interface StepResult {
  step_index: number;
  task_id?: string;
  gate_kind?: string;
  outcome: TaskOutcome | { status: "gate_approved" } | { status: "gate_rejected" } | { status: "skipped" };
  follow_ups_used: number;
  retries_used: number;
  elapsed_ms: number;
}

export interface StepCompleteEvent {
  workflow_id: string;
  step_index: number;
  step_type: "delegate" | "gate";
  task_id?: string;
  gate_kind?: string;
  outcome_status: string;
  follow_ups_used: number;
  retries_used: number;
  elapsed_ms: number;
}

export interface WorkflowResult {
  workflow_id: string;
  status: "completed" | "escalated" | "gate_rejected" | "error";
  steps: StepResult[];
  total_elapsed_ms: number;
  escalation_reason?: string;
  error_message?: string;
}

export interface WorkflowRegistries {
  workflowRegistry?: Record<string, WorkflowContractLike>;
  taskRegistry?: Record<string, TaskContractLike>;
  agentRegistry?: Record<string, AgentContractLike>;
  handoffSchemas?: Record<string, z.ZodType>;
}

// ---------------------------------------------------------------------------
// Runner
// ---------------------------------------------------------------------------

/**
 * Resolve a WorkflowInvocation into the legacy (workflowId, WorkflowRunOptions) pair.
 * If a typed handoff is provided, serialise its payload as the user_request
 * context and attach a structured handoff_context prefix so the prompt builder
 * can reference it.
 */
function resolveInvocation(invocation: WorkflowInvocation): {
  workflowId: string;
  options: WorkflowRunOptions;
} {
  let userRequest: string;
  if (invocation.handoff) {
    const h = invocation.handoff;
    userRequest = invocation.user_request
      ? invocation.user_request
      : typeof h.payload === "string"
        ? h.payload
        : JSON.stringify(h.payload, null, 2);
  } else {
    userRequest = invocation.user_request ?? "";
  }

  const ctx = invocation.context;
  const variables: Record<string, unknown> = { ...ctx?.variables };
  if (ctx?.cwd) variables.cwd = ctx.cwd;
  if (ctx?.environment) variables.environment = ctx.environment;
  if (ctx?.artifacts) variables.artifacts = ctx.artifacts;

  return {
    workflowId: invocation.workflow,
    options: {
      user_request: userRequest,
      maxFollowUps: invocation.runtime?.maxFollowUps,
      maxRetries: invocation.runtime?.maxRetries,
      variables: Object.keys(variables).length > 0 ? variables : undefined,
      onGate: invocation.hooks?.onGate,
      onStepComplete: invocation.hooks?.onStepComplete,
      onOptionalStep: invocation.hooks?.onOptionalStep,
      progressOutput: invocation.progressOutput,
    },
  };
}

/** @deprecated Use the WorkflowInvocation overload for typed handoff input. */
export function runWorkflow(
  adapter: AdapterOrFactory,
  workflowId: string,
  options: WorkflowRunOptions,
  registries?: WorkflowRegistries,
): Promise<WorkflowResult>;

/** Structured invocation with typed handoff envelope. */
export function runWorkflow(
  adapter: AdapterOrFactory,
  invocation: WorkflowInvocation,
  registries?: WorkflowRegistries,
): Promise<WorkflowResult>;

export async function runWorkflow(
  adapterOrFactory: AdapterOrFactory,
  workflowIdOrInvocation: string | WorkflowInvocation,
  optionsOrRegistries?: WorkflowRunOptions | WorkflowRegistries,
  maybeRegistries?: WorkflowRegistries,
): Promise<WorkflowResult> {
  const { adapter: primaryAdapter, factory: adapterFactory } = resolveAdapter(adapterOrFactory);
  let workflowId: string;
  let options: WorkflowRunOptions;
  let registries: WorkflowRegistries | undefined;
  let invocationHandoff: HandoffInput | undefined;

  if (typeof workflowIdOrInvocation === "string") {
    workflowId = workflowIdOrInvocation;
    options = optionsOrRegistries as WorkflowRunOptions;
    registries = maybeRegistries;
  } else {
    const resolved = resolveInvocation(workflowIdOrInvocation);
    workflowId = resolved.workflowId;
    options = resolved.options;
    registries = optionsOrRegistries as WorkflowRegistries | undefined;
    invocationHandoff = workflowIdOrInvocation.handoff;
  }

  // --- G9: Validate input handoff against schema if available ---
  if (invocationHandoff && registries?.handoffSchemas) {
    const inputSchema = registries.handoffSchemas[invocationHandoff.type];
    if (inputSchema) {
      const validation = inputSchema.safeParse(invocationHandoff.payload);
      if (!validation.success) {
        return {
          workflow_id: workflowId,
          status: "error",
          steps: [],
          total_elapsed_ms: 0,
          error_message: `Input handoff validation failed for "${invocationHandoff.type}": ${validation.error.message}`,
        };
      }
    }
  }

  const wfReg = registries?.workflowRegistry ?? {};
  const workflow = wfReg[workflowId];
  if (!workflow) {
    return {
      workflow_id: workflowId,
      status: "error",
      steps: [],
      total_elapsed_ms: 0,
      error_message: `Unknown workflow: ${workflowId}`,
    };
  }

  await pluginRegistry.runBeforeWorkflow(workflowId, options.user_request);

  const workflowStart = Date.now();

  // Build step identity map: step index → task ID (delegate) or gate_kind (gate)
  const stepId = (i: number): string => {
    const s = workflow.steps[i];
    return s.type === "delegate" ? s.task : `gate:${s.gate_kind}`;
  };

  // Resolve depends_on: if specified (even as empty array) use it, otherwise depend on all preceding steps
  const depsByIndex: number[][] = workflow.steps.map((step, i) => {
    if (step.depends_on !== undefined) {
      return step.depends_on.map((dep) => {
        const idx = workflow.steps.findIndex((s, j) => j < i && stepId(j) === dep);
        if (idx === -1) {
          throw new Error(`depends_on "${dep}" in step ${i} (${stepId(i)}) not found among preceding steps`);
        }
        return idx;
      });
    }
    // Default: depend on all preceding steps (preserves sequential behaviour)
    return i === 0 ? [] : Array.from({ length: i }, (_, k) => k);
  });

  // DAG scheduler state
  const completed = new Set<number>();
  const stepResults: StepResult[] = new Array(workflow.steps.length);
  const stepContexts: string[] = new Array(workflow.steps.length).fill("");
  let abortResult: WorkflowResult | undefined;

  /** Build accumulated context for a step from its dependency chain. */
  function contextFor(stepIndex: number): string {
    const deps = depsByIndex[stepIndex];
    if (deps.length === 0) return "";
    return deps
      .filter((d) => completed.has(d))
      .map((d) => stepContexts[d])
      .filter(Boolean)
      .join("");
  }

  /** Execute a single step (delegate or gate). */
  async function executeStep(i: number, stepAdapter: SdkAdapter): Promise<StepResult> {
    const step = workflow.steps[i];
    const stepStart = Date.now();

    if (step.type === "gate") {
      return handleGateStep(step, Object.values(stepResults).filter(Boolean), options, i, stepAdapter);
    }

    // Optional steps
    if (step.optional) {
      const ctx: TaskContext = { user_request: options.user_request, prior_context: contextFor(i), variables: options.variables };
      const shouldRun = options.onOptionalStep
        ? await options.onOptionalStep(step, ctx)
        : false;

      if (!shouldRun) {
        return {
          step_index: i,
          task_id: step.task,
          outcome: { status: "skipped" },
          follow_ups_used: 0,
          retries_used: 0,
          elapsed_ms: Date.now() - stepStart,
        };
      }
    }

    // Execute task with followUp/retry recovery
    const context: TaskContext = {
      user_request: options.user_request,
      prior_context: contextFor(i),
      variables: options.variables,
      ...(invocationHandoff ? { handoff_input: invocationHandoff } : {}),
    };
    const taskResult: TaskRunResult = await runTask(stepAdapter, step.task, context, {
      maxFollowUps: options.maxFollowUps ?? step.max_follow_ups ?? 2,
      maxRetries: options.maxRetries ?? step.max_retries ?? 0,
      taskRegistry: registries?.taskRegistry,
      agentRegistry: registries?.agentRegistry,
      handoffSchemas: registries?.handoffSchemas,
      retry: step.retry,
      progressOutput: options.progressOutput,
    });
    return {
      step_index: i,
      task_id: step.task,
      outcome: taskResult.outcome,
      follow_ups_used: taskResult.follow_ups_used,
      retries_used: taskResult.retries_used,
      elapsed_ms: Date.now() - stepStart,
    };
  }

  // DAG execution loop: on each iteration, find all steps whose deps are satisfied
  // and run them concurrently.
  while (completed.size < workflow.steps.length) {
    const ready: number[] = [];
    for (let i = 0; i < workflow.steps.length; i++) {
      if (completed.has(i)) continue;
      if (depsByIndex[i].every((d) => completed.has(d))) {
        ready.push(i);
      }
    }

    if (ready.length === 0) {
      // Should not happen with valid DAG; safeguard against cycles
      abortResult = {
        workflow_id: workflow.id,
        status: "error",
        steps: stepResults.filter(Boolean),
        total_elapsed_ms: Date.now() - workflowStart,
        error_message: "Dependency cycle detected: no steps are ready to execute",
      };
      break;
    }

    const batchResults = await Promise.all(ready.map(async (i) => {
      const step = workflow.steps[i];
      const modelClass = step.type === "delegate"
        ? (registries?.taskRegistry?.[step.task]?.model_class as ModelClass | undefined)
        : undefined;

      let stepAdapter: SdkAdapter;
      if (ready.length > 1 && adapterFactory) {
        const result = adapterFactory(modelClass);
        stepAdapter = (result && typeof (result as Promise<SdkAdapter>).then === "function")
          ? await (result as Promise<SdkAdapter>)
          : result as SdkAdapter;
      } else if (modelClass && adapterFactory) {
        const result = adapterFactory(modelClass);
        stepAdapter = (result && typeof (result as Promise<SdkAdapter>).then === "function")
          ? await (result as Promise<SdkAdapter>)
          : result as SdkAdapter;
      } else {
        stepAdapter = primaryAdapter;
      }
      return executeStep(i, stepAdapter);
    }));

    for (let b = 0; b < ready.length; b++) {
      const i = ready[b];
      const result = batchResults[b];
      const step = workflow.steps[i];

      stepResults[i] = result;
      completed.add(i);
      emitStepEvent(options, workflow.id, i, step, result);

      // Store context fragment for dependants
      if (result.outcome.status === "success") {
        const data = (result.outcome as { data: unknown }).data;
        stepContexts[i] = `\n\n--- Step ${i + 1}: ${stepId(i)} ---\n` +
          (typeof data === "string" ? data : JSON.stringify(data, null, 2));
      }

      // Abort-worthy outcomes
      if (result.outcome.status === "gate_rejected") {
        abortResult = {
          workflow_id: workflow.id,
          status: "gate_rejected",
          steps: stepResults.filter(Boolean),
          total_elapsed_ms: Date.now() - workflowStart,
        };
      } else if (result.outcome.status === "escalation") {
        abortResult = {
          workflow_id: workflow.id,
          status: "escalated",
          steps: stepResults.filter(Boolean),
          total_elapsed_ms: Date.now() - workflowStart,
          escalation_reason: (result.outcome as { reason: string }).reason,
        };
      } else if (result.outcome.status === "error" || result.outcome.status === "validation_error") {
        const taskId = step.type === "delegate" ? step.task : step.gate_kind;
        abortResult = {
          workflow_id: workflow.id,
          status: "escalated",
          steps: stepResults.filter(Boolean),
          total_elapsed_ms: Date.now() - workflowStart,
          escalation_reason: `Task "${taskId}" failed after ${result.follow_ups_used} follow-ups and ${result.retries_used} retries`,
        };
      }
    }

    if (abortResult) break;
  }

  const wfResult: WorkflowResult = abortResult ?? {
    workflow_id: workflow.id,
    status: "completed",
    steps: stepResults.filter(Boolean),
    total_elapsed_ms: Date.now() - workflowStart,
  };
  await pluginRegistry.runAfterWorkflow(workflow.id, wfResult);
  return wfResult;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function handleGateStep(
  step: GateStep,
  priorResults: StepResult[],
  options: WorkflowRunOptions,
  stepIndex: number,
  adapter?: SdkAdapter,
): Promise<StepResult> {
  const start = Date.now();

  if (options.onGate) {
    const approved = await options.onGate(step.gate_kind, step.description, priorResults);
    return {
      step_index: stepIndex,
      gate_kind: step.gate_kind,
      outcome: { status: approved ? "gate_approved" : "gate_rejected" },
      follow_ups_used: 0,
      retries_used: 0,
      elapsed_ms: Date.now() - start,
    };
  }

  // LLM-based gate evaluation using the description as the condition
  if (adapter && step.description) {
    const approved = await evaluateGateWithLlm(adapter, step, priorResults);
    return {
      step_index: stepIndex,
      gate_kind: step.gate_kind,
      outcome: { status: approved ? "gate_approved" : "gate_rejected" },
      follow_ups_used: 0,
      retries_used: 0,
      elapsed_ms: Date.now() - start,
    };
  }

  // Fallback: no onGate, no adapter or no description — auto-approve with warning
  return {
    step_index: stepIndex,
    gate_kind: step.gate_kind,
    outcome: { status: "gate_approved" },
    follow_ups_used: 0,
    retries_used: 0,
    elapsed_ms: Date.now() - start,
  };
}

/**
 * Use the LLM adapter to evaluate a gate condition.
 * Sends a lightweight prompt with the gate description and prior step outcomes,
 * expecting a JSON response with { "approved": boolean, "reason": string }.
 */
async function evaluateGateWithLlm(
  adapter: SdkAdapter,
  step: GateStep,
  priorResults: StepResult[],
): Promise<boolean> {
  const priorContext = priorResults
    .map((r) => {
      const id = r.task_id ?? r.gate_kind ?? `step_${r.step_index}`;
      const data = "data" in r.outcome ? (r.outcome as { data: unknown }).data : undefined;
      return `- ${id}: status=${r.outcome.status}${data ? `, data=${typeof data === "string" ? data : JSON.stringify(data)}` : ""}`;
    })
    .join("\n");

  const prompt = buildGateEvaluationPrompt(step, priorContext);

  try {
    const response = await adapter.send(prompt, { readonly: true });
    return parseGateEvaluationResponse(response);
  } catch {
    // On LLM failure, reject the gate (fail-closed)
    return false;
  }
}

export function buildGateEvaluationPrompt(step: GateStep, priorContext: string): string {
  return `You are a gate evaluator in a workflow pipeline. Your job is to decide whether the workflow should proceed based on prior step results.

## Gate
- kind: ${step.gate_kind}
- condition: ${step.description}

## Prior Step Results
${priorContext || "(no prior results)"}

## Instructions
Evaluate whether the gate condition is satisfied based on the prior step results.
Respond with ONLY a JSON object (no markdown fences, no extra text):

{"approved": true, "reason": "brief explanation"}

or

{"approved": false, "reason": "brief explanation"}`;
}

export function parseGateEvaluationResponse(response: string): boolean {
  // Try to extract JSON from the response
  const jsonMatch = response.match(/\{[\s\S]*?"approved"\s*:\s*(true|false)[\s\S]*?\}/);
  if (jsonMatch) {
    try {
      const parsed = JSON.parse(jsonMatch[0]);
      return parsed.approved === true;
    } catch {
      // fall through
    }
  }

  // Heuristic fallback: look for explicit approval/rejection keywords
  const lower = response.toLowerCase();
  if (lower.includes('"approved": true') || lower.includes('"approved":true')) {
    return true;
  }
  if (lower.includes('"approved": false') || lower.includes('"approved":false')) {
    return false;
  }

  // Fail-closed: if we can't parse, reject
  return false;
}

function emitStepEvent(
  options: WorkflowRunOptions,
  workflowId: string,
  stepIndex: number,
  step: DelegateStep | GateStep,
  result: StepResult,
): void {
  options.onStepComplete?.({
    workflow_id: workflowId,
    step_index: stepIndex,
    step_type: step.type,
    task_id: step.type === "delegate" ? (step as DelegateStep).task : undefined,
    gate_kind: step.type === "gate" ? (step as GateStep).gate_kind : undefined,
    outcome_status: result.outcome.status,
    follow_ups_used: result.follow_ups_used,
    retries_used: result.retries_used,
    elapsed_ms: result.elapsed_ms,
  });
}
